<script setup lang="ts">
const props = defineProps<{
	open: boolean;
}>();

const emit = defineEmits<{
	close: [];
	saved: [];
}>();

const config = useRuntimeConfig();
const { user, customer } = useAuth();
const { authFetch, getToken } = useAuthFetch();

const first_name = ref('');
const last_name = ref('');
const phone = ref('');
const date_of_birth = ref('');
const gender = ref<string>('');

const avatarFile = ref<File | null>(null);
const removeAvatar = ref(false);
const fileInputRef = ref<HTMLInputElement | null>(null);

const directusUrl = config.public.directusUrl as string;
const currentAvatarUrl = computed(() => {
	const id = user.value?.avatar;
	if (!id || !directusUrl) return null;
	return `${directusUrl}/assets/${id}`;
});
const avatarPreviewObjectUrl = ref<string | null>(null);
watch(avatarFile, (file) => {
	if (avatarPreviewObjectUrl.value) URL.revokeObjectURL(avatarPreviewObjectUrl.value);
	avatarPreviewObjectUrl.value = file ? URL.createObjectURL(file) : null;
}, { immediate: true });
onBeforeUnmount(() => {
	if (avatarPreviewObjectUrl.value) URL.revokeObjectURL(avatarPreviewObjectUrl.value);
});
const previewUrl = computed(() => {
	if (removeAvatar.value) return null;
	if (avatarFile.value && avatarPreviewObjectUrl.value) return avatarPreviewObjectUrl.value;
	return currentAvatarUrl.value;
});

const saving = ref(false);
const error = ref<string | null>(null);

watch(
	() => [props.open, user.value, customer.value] as const,
	([open, u, c]) => {
		if (open && (u || c)) {
			first_name.value = (c?.first_name ?? u?.first_name) ?? '';
			last_name.value = (c?.last_name ?? u?.last_name) ?? '';
			phone.value = c?.phone ?? '';
			date_of_birth.value = c?.date_of_birth ? c.date_of_birth.slice(0, 10) : '';
			gender.value = c?.gender ?? '';
			avatarFile.value = null;
			removeAvatar.value = false;
			error.value = null;
		}
	},
	{ immediate: true }
);

function onAvatarFileChange(e: Event) {
	const input = e.target as HTMLInputElement;
	const file = input.files?.[0];
	if (file && file.type.startsWith('image/')) {
		avatarFile.value = file;
		removeAvatar.value = false;
	}
	input.value = '';
}

function triggerFileInput() {
	fileInputRef.value?.click();
}

function clearAvatar() {
	avatarFile.value = null;
	removeAvatar.value = true;
}

async function save() {
	error.value = null;
	saving.value = true;
	try {
		const body: Record<string, unknown> = {
			first_name: first_name.value || null,
			last_name: last_name.value || null,
			phone: phone.value || null,
			date_of_birth: date_of_birth.value || null,
			gender: gender.value || null,
		};
		if (removeAvatar.value) {
			body.avatar = null;
		} else if (avatarFile.value) {
			const formData = new FormData();
			formData.append('file', avatarFile.value);
			const token = getToken();
			const res = await $fetch<{ id: string }>('/api/area-riservata/profile/avatar', {
				method: 'POST',
				body: formData,
				headers: token ? { Authorization: `Bearer ${token}` } : {},
			});
			body.avatar = res.id;
		}
		await authFetch('/api/area-riservata/profile', {
			method: 'PATCH',
			body,
		});
		emit('saved');
	} catch (err: any) {
		error.value = err?.data?.statusMessage || err?.message || 'Errore durante il salvataggio';
	} finally {
		saving.value = false;
	}
}

function close() {
	emit('close');
}

const genderOptions = [
	{ value: '', label: '—' },
	{ value: 'male', label: 'Uomo' },
	{ value: 'female', label: 'Donna' },
	{ value: 'transgender', label: 'Transgender' },
	{ value: 'agender', label: 'Non binario' },
	{ value: 'not-specified', label: 'Non specificato' },
];
</script>

<template>
	<Teleport to="body">
		<div
			v-if="open"
			class="fixed inset-0 z-50 flex"
			aria-modal="true"
			role="dialog"
			aria-labelledby="panel-profilo-title"
		>
			<button
				type="button"
				class="absolute inset-0 bg-black/50 transition-opacity"
				aria-label="Chiudi"
				@click="close"
			/>
			<div
				class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
				@click.stop
			>
				<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
					<h2 id="panel-profilo-title" class="text-lg font-semibold text-gray-900">
						Modifica profilo
					</h2>
					<button
						type="button"
						class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
						aria-label="Chiudi"
						@click="close"
					>
						<span class="material-symbols-outlined">close</span>
					</button>
				</div>
				<div class="flex-1 overflow-y-auto px-4 py-4">
					<form class="space-y-4" @submit.prevent="save">
						<div v-if="error" class="rounded-md bg-red-50 p-3 text-sm text-red-800">
							{{ error }}
						</div>
						<!-- Avatar -->
						<div class="flex flex-col gap-3">
							<label class="block text-sm font-medium text-gray-700">Avatar</label>
							<div class="flex items-center gap-4">
								<div class="h-20 w-20 shrink-0 overflow-hidden rounded-full bg-gray-200 flex items-center justify-center">
									<img
										v-if="previewUrl"
										:src="previewUrl"
										alt="Avatar"
										class="h-full w-full object-cover"
									/>
									<span v-else class="material-symbols-outlined text-4xl text-gray-500">person</span>
								</div>
								<div class="flex flex-col gap-2">
									<input
										ref="fileInputRef"
										type="file"
										accept="image/*"
										class="hidden"
										@change="onAvatarFileChange"
									/>
									<button
										type="button"
										class="rounded-none border-2 border-black px-4 py-2 text-sm font-button uppercase tracking-wide text-black transition hover:bg-gray-50 hover:text-[#A21B1F]"
										@click="triggerFileInput"
									>
										Carica nuova immagine
									</button>
									<button
										v-if="previewUrl"
										type="button"
										class="text-left text-sm font-button uppercase tracking-wide text-gray-600 underline hover:text-gray-800"
										@click="clearAvatar"
									>
										Rimuovi avatar
									</button>
								</div>
							</div>
						</div>
						<div>
							<label for="profilo-first_name" class="block text-sm font-medium text-gray-700">Nome</label>
							<input
								id="profilo-first_name"
								v-model="first_name"
								type="text"
								autocomplete="given-name"
								class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#A21B1F] focus:ring-[#A21B1F] sm:text-sm"
							/>
						</div>
						<div>
							<label for="profilo-last_name" class="block text-sm font-medium text-gray-700">Cognome</label>
							<input
								id="profilo-last_name"
								v-model="last_name"
								type="text"
								autocomplete="family-name"
								class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#A21B1F] focus:ring-[#A21B1F] sm:text-sm"
							/>
						</div>
						<div>
							<label for="profilo-phone" class="block text-sm font-medium text-gray-700">Telefono</label>
							<input
								id="profilo-phone"
								v-model="phone"
								type="tel"
								autocomplete="tel"
								class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#A21B1F] focus:ring-[#A21B1F] sm:text-sm"
							/>
						</div>
						<div>
							<label for="profilo-date_of_birth" class="block text-sm font-medium text-gray-700">Data di nascita</label>
							<input
								id="profilo-date_of_birth"
								v-model="date_of_birth"
								type="date"
								class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#A21B1F] focus:ring-[#A21B1F] sm:text-sm"
							/>
						</div>
						<div>
							<label for="profilo-gender" class="block text-sm font-medium text-gray-700">Genere</label>
							<select
								id="profilo-gender"
								v-model="gender"
								class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#A21B1F] focus:ring-[#A21B1F] sm:text-sm"
							>
								<option v-for="opt in genderOptions" :key="opt.value" :value="opt.value">
									{{ opt.label }}
								</option>
							</select>
						</div>
						<div class="flex gap-3 pt-2">
							<button
								type="button"
								class="flex-1 inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-black bg-white border-2 border-black hover:bg-gray-50 hover:text-[#A21B1F] rounded-none transition-colors uppercase"
								@click="close"
							>
								Annulla
							</button>
							<button
								type="submit"
								:disabled="saving"
								class="flex-1 inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-white bg-black hover:bg-black/90 rounded-none transition-colors uppercase disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{{ saving ? 'SALVATAGGIO...' : 'SALVA' }}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	</Teleport>
</template>
